import { useState, useEffect, useRef, useCallback } from "react";
import { FaChevronDown } from "react-icons/fa";
import { getLeadersByEmail } from "../../api/payment";

const EmailSelector = ({
  value,
  onChange,
  error,
  label = "Seleccionar Propietario (Correo Electrónico)",
  placeholder = "Buscar por correo electrónico",
}) => {
  const [options, setOptions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const loadOptions = useCallback(async (searchValue = "") => {
    try {
      setLoading(true);
      const response = await getLeadersByEmail(searchValue, 1, 10);
      setOptions(response.data || []);
    } catch (error) {
      console.error("Error loading leaders:", error);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedSearch = useCallback(
    (searchValue) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        loadOptions(searchValue);
      }, 300);
    },
    [loadOptions]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    onChange(null);
    setShowDropdown(true);
    debouncedSearch(inputValue);
  };

  const handleInputFocus = () => {
    setShowDropdown(true);
    if (options.length === 0) {
      loadOptions(searchTerm);
    }
  };

  const handleOptionSelect = (option) => {
    setSearchTerm(option.email || "");
    onChange(option);
    setShowDropdown(false);
  };

  return (
    <div className="input-group">
      <label className="input-group__label">
        {label} <span className="input-group__required">*</span>
      </label>
      <div className={`autocomplete-container ${showDropdown ? "active" : ""}`}>
        <input
          ref={inputRef}
          type="text"
          name="email"
          placeholder={placeholder}
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          className={`input-group__input ${
            error ? "input-group__input--error" : ""
          }`}
          autoComplete="off"
        />
        <FaChevronDown
          className={`autocomplete-icon ${
            showDropdown ? "autocomplete-icon--rotated" : ""
          }`}
          onClick={() => setShowDropdown(!showDropdown)}
        />

        {showDropdown && (
          <div ref={dropdownRef} className="autocomplete-dropdown">
            {loading ? (
              <div className="autocomplete-loading">
                <span>Buscando propietarios...</span>
              </div>
            ) : options.length > 0 ? (
              options.map((option, index) => (
                <div
                  key={index}
                  className={`autocomplete-option ${
                    value && value.email === option.email
                      ? "autocomplete-option--selected"
                      : ""
                  }`}
                  onClick={() => handleOptionSelect(option)}
                >
                  <div className="option-code">{option.email}</div>
                  {(option.first_name || option.last_name) && (
                    <div className="option-name">
                      {`${option.first_name || ""} ${
                        option.last_name || ""
                      }`.trim()}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="autocomplete-no-results">
                {searchTerm
                  ? `No se encontraron propietarios con "${searchTerm}"`
                  : "No hay propietarios disponibles"}
              </div>
            )}
          </div>
        )}
      </div>
      {error && <span className="input-group__error">{error}</span>}
    </div>
  );
};

export default EmailSelector;
